<template>
  <v-app>
    <!-- Header mejorado -->
    <v-app-bar
      elevation="2"
      color="white"
      prominent
      class="header-bar"
    >
      <template v-slot:prepend>
        <img
          alt="Logo"
          class="brand-logo"
          :src="SHOP.brandImage"
        >
      </template>
      
      <v-spacer></v-spacer>
      
      <template v-slot:append>
        <!-- Cart Badge -->
        <v-badge
          v-if="ORDER.price"
          :content="ORDER.detail.length"
          color="secondary"
          class="cart-badge-fixed"
        >
          <v-btn
            icon
            size="large"
            @click="toggleOrderDetail"
            class="cart-button"
          >
            <v-icon color="primary">mdi-cart</v-icon>
          </v-btn>
        </v-badge>
        
        <v-btn
          icon
          @click="showCountrySelection = true"
          class="country-selector"
        >
          <img
            alt="País"
            :src="SHOP.countryImage"
            style="height: 32px; border-radius: 50%;"
          >
        </v-btn>
      </template>
    </v-app-bar>

    <!-- Hero Section mejorado -->
    <v-container fluid class="hero-section pa-0">
      <div class="hero-content">
        <v-container>
          <v-row align="center" justify="center">
            <v-col cols="12" class="text-center">
              <div class="hero-badge mb-3">
                <v-icon size="20" class="mr-1">mdi-certificate</v-icon>
                <span>Calidad Premium</span>
              </div>
              <h1 class="hero-title">
                🌱 Alimentación Saludable y Vegana
              </h1>
              <p class="hero-subtitle">
                Productos 100% naturales elaborados con ingredientes seleccionados
              </p>
              <p class="hero-description">
                Sin conservantes • Sin aditivos • Congelados para mantener su frescura
              </p>
              
              <div class="badges-container">
                <v-chip
                  size="large"
                  class="hero-chip chip-vegano"
                >
                  <v-icon start size="20">mdi-leaf</v-icon>
                  <span class="chip-text">100% Vegano</span>
                </v-chip>
                <v-chip
                  size="large"
                  class="hero-chip chip-congelado"
                >
                  <v-icon start size="20">mdi-snowflake</v-icon>
                  <span class="chip-text">Recién Congelado</span>
                </v-chip>
                <v-chip
                  size="large"
                  class="hero-chip chip-saludable"
                >
                  <v-icon start size="20">mdi-heart-pulse</v-icon>
                  <span class="chip-text">Nutrición Completa</span>
                </v-chip>
              </div>
              
              <div class="trust-indicators mt-6">
                <div class="trust-item">
                  <v-icon size="24" class="trust-icon">mdi-shield-check</v-icon>
                  <span>Sin Conservantes</span>
                </div>
                <div class="trust-item">
                  <v-icon size="24" class="trust-icon">mdi-truck-fast</v-icon>
                  <span>Envío Rápido</span>
                </div>
                <div class="trust-item">
                  <v-icon size="24" class="trust-icon">mdi-star</v-icon>
                  <span>Calidad Garantizada</span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-container>

    <!-- Navegación entre categorías -->
    <div v-if="activeCategoriesCount > 1" class="categories-nav-sticky">
      <v-container>
        <div class="categories-nav-content">
          <v-chip
            v-if="SHOP.burgers.length"
            @click="scrollToCategory('medallones')"
            color="primary"
            variant="tonal"
            size="large"
            class="category-nav-chip"
          >
            <v-icon start>mdi-hamburger</v-icon>
            <span>Medallones</span>
          </v-chip>
          <v-chip
            v-if="SHOP.dressings.length"
            @click="scrollToCategory('aderezos')"
            color="secondary"
            variant="tonal"
            size="large"
            class="category-nav-chip"
          >
            <v-icon start>mdi-silverware-spoon</v-icon>
            <span>Aderezos</span>
          </v-chip>
          <v-chip
            v-if="SHOP.others.length"
            @click="scrollToCategory('otros')"
            color="accent"
            variant="tonal"
            size="large"
            class="category-nav-chip"
          >
            <v-icon start>mdi-food-variant</v-icon>
            <span>Otros</span>
          </v-chip>
        </div>
      </v-container>
    </div>

    <v-container class="main__container py-8">
      <div v-if="SHOP.burgers.length" id="medallones" class="category-section">
        <div class="category-header">
          <v-icon size="40" color="primary" class="mr-3">mdi-hamburger</v-icon>
          <div>
            <h2 class="category-title">Medallones Congelados</h2>
            <p class="category-subtitle">Sabor y nutrición en cada bocado</p>
          </div>
        </div>
        <v-row>
          <v-col
            v-for="burger in SHOP.burgers"
            :key="burger.img"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <BurgerCard
              :id="burger.id"
              :name="burger.name"
              :image="burger.img"
              :price="burger.price"
              :order-count="ORDER.getProductCount(burger.id)"
              @show-detail="setProductDetail(burger)"
              @add="ORDER.addProduct(burger.id)"
              @delete="ORDER.deleteProduct(burger.id)"
            />
          </v-col>
        </v-row>
      </div>

      <div v-if="SHOP.dressings.length" id="aderezos" class="category-section">
        <div class="category-header">
          <v-icon size="40" color="secondary" class="mr-3">mdi-silverware-spoon</v-icon>
          <div>
            <h2 class="category-title">Aderezos Veganos</h2>
            <p class="category-subtitle">Sabores naturales para tus comidas</p>
          </div>
        </div>
        <v-row>
          <v-col
            v-for="dressing in SHOP.dressings"
            :key="dressing.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <DressingCard
              :id="dressing.id"
              :name="dressing.name"
              :image="dressing.img"
              :price="dressing.price"
              :order-count="ORDER.getProductCount(dressing.id)"
              @show-detail="setProductDetail(dressing)"
              @add="ORDER.addProduct(dressing.id)"
              @delete="ORDER.deleteProduct(dressing.id)"
            />
          </v-col>
        </v-row>
      </div>

      <div v-if="SHOP.others.length" id="otros" class="category-section">
        <div class="category-header">
          <v-icon size="40" color="accent" class="mr-3">mdi-food-variant</v-icon>
          <div>
            <h2 class="category-title">Otros Productos</h2>
            <p class="category-subtitle">Snacks, barritas y más opciones saludables</p>
          </div>
        </div>
        <v-row>
          <v-col
            v-for="other in SHOP.others"
            :key="other.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            :id="other.id"
          >
            <OtherCard
              :id="other.id"
              :name="other.name"
              :subtitle="other.subtitle"
              :image="other.img"
              :price="other.price"
              :unity="other.unity"
              :order-count="ORDER.getProductCount(other.id)"
              @show-detail="setProductDetail(other)"
              @add="ORDER.addProduct(other.id)"
              @delete="ORDER.deleteProduct(other.id)"
            />
          </v-col>
        </v-row>
      </div>
    </v-container>

    <!-- Footer -->
    <v-footer class="footer-section">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center">
            <v-icon size="30" color="white">mdi-leaf</v-icon>
            <p class="mt-3 text-body-1">
              <strong>Alimentación saludable para todos</strong>
            </p>
            <p class="text-body-2">
              Productos 100% vegetales, sin conservantes ni aditivos
            </p>
            <v-divider class="my-4"></v-divider>
            <p class="text-caption">
              © 2026 Veggie - Todos los derechos reservados
            </p>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>

    <!-- Bottom Navigation mejorado -->
    <v-bottom-navigation
      v-if="!showProductDetail"
      elevation="8"
      height="auto"
      class="bottom-nav-enhanced"
    >
      <div v-if="ORDER.price" class="bottom-nav-content">
        <div class="order-summary-clean" @click="toggleOrderDetail">
          <div class="order-info">
            <span class="order-items">{{ ORDER.detail.length }} productos</span>
            <span class="order-total">${{ ORDER.price }}</span>
          </div>
        </div>
        <div class="action-buttons-clean">
          <v-btn
            @click="showCancelQuestion = true"
            class="action-btn-clean cancel-btn-clean"
          >
            <v-icon>mdi-close</v-icon>
            <span>Cancelar</span>
          </v-btn>
          <v-btn
            @click="ORDER.confirm()"
            class="action-btn-clean confirm-btn-clean"
          >
            <v-icon>mdi-whatsapp</v-icon>
            <span>Confirmar</span>
          </v-btn>
        </div>
      </div>
      <div v-else class="bottom-nav-content-single">
        <v-btn
          @click="SHOP.sendMessage()"
          block
          size="large"
          elevation="6"
          class="whatsapp-button"
        >
          <v-icon start size="24" class="whatsapp-icon">mdi-whatsapp</v-icon>
          <span class="whatsapp-text">Contactarme por WhatsApp</span>
        </v-btn>
      </div>
    </v-bottom-navigation>

    <!-- Modal de Detalle de Producto mejorado -->
    <v-dialog
      v-model="showProductDetail"
      max-width="600"
      transition="dialog-bottom-transition"
    >
      <v-card class="product-detail-card">
        <v-card-title class="product-detail-header">
          <v-icon color="success" class="mr-2">mdi-leaf-circle</v-icon>
          {{ productDetail.title }}
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="py-6">
          <div class="price-highlight mb-4">
            <v-icon color="success">mdi-currency-usd</v-icon>
            <span class="price-large">${{ productDetail.price }}</span>
            <span class="price-unit-text">por unidad</span>
          </div>
          
          <div class="description-section">
            <h4 class="mb-2">
              <v-icon size="small" color="info">mdi-information</v-icon>
              Descripción
            </h4>
            <p class="description-text">{{ productDetail.description }}</p>
          </div>

          <v-chip-group class="mt-4">
            <v-chip color="success" variant="tonal">
              <v-icon start>mdi-leaf</v-icon>
              100% Vegano
            </v-chip>
            <v-chip color="info" variant="tonal">
              <v-icon start>mdi-check-circle</v-icon>
              Sin Conservantes
            </v-chip>
            <v-chip color="secondary" variant="tonal">
              <v-icon start>mdi-heart-pulse</v-icon>
              Saludable
            </v-chip>
          </v-chip-group>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="elevated"
            size="large"
            @click="showProductDetail = false"
          >
            <v-icon start>mdi-close</v-icon>
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Detalle de Pedido mejorado -->
    <v-dialog
      v-model="showOrderDetail"
      max-width="600"
      scrollable
      transition="dialog-bottom-transition"
    >
      <v-card class="order-detail-card">
        <v-card-title class="order-detail-header">
          <div class="header-content">
            <div class="header-title">
              <v-icon size="28" color="primary">mdi-cart-check</v-icon>
              <span class="ml-2">Tu Pedido</span>
            </div>
            <div class="total-price-display">
              <span class="total-label">TOTAL</span>
              <span class="total-amount">${{ ORDER.price }}</span>
            </div>
          </div>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="pa-0" style="max-height: 60vh;">
          <v-list>
            <v-list-item
              v-for="(product, index) in ORDER.detail"
              :key="product.id"
              class="order-item py-3"
            >
              <div class="order-item-content">
                <div class="item-info">
                  <v-avatar color="primary" size="36" class="mr-3">
                    <span class="font-weight-bold">{{ ORDER.getProductCount(product.id) }}</span>
                  </v-avatar>
                  <div class="item-details">
                    <div class="item-name">{{ `${!product.category ? '' : `${product.category} de`} ${product.name}` }}</div>
                    <div class="item-price">
                      <span>${{ product.price }}</span>
                      <v-chip size="x-small" color="success" class="ml-2">
                        Subtotal: ${{ product.count * product.price }}
                      </v-chip>
                    </div>
                  </div>
                </div>
                <div class="item-actions-cart">
                  <v-btn
                    icon
                    size="x-small"
                    color="error"
                    variant="tonal"
                    @click="ORDER.deleteProduct(product.id)"
                  >
                    <v-icon size="18">mdi-minus</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    size="x-small"
                    color="success"
                    variant="tonal"
                    @click="ORDER.addProduct(product.id)"
                  >
                    <v-icon size="18">mdi-plus</v-icon>
                  </v-btn>
                </div>
              </div>
              <v-divider v-if="index < ORDER.detail.length - 1" class="mt-3"></v-divider>
            </v-list-item>
          </v-list>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-card-actions class="pa-3 order-actions-footer">
          <v-row no-gutters class="w-100">
            <v-col cols="12" class="mb-2">
              <v-btn
                color="success"
                variant="elevated"
                size="large"
                block
                @click="ORDER.confirm(); showOrderDetail = false"
                class="confirm-btn"
              >
                <v-icon start size="22">mdi-whatsapp</v-icon>
                <span>Confirmar Pedido</span>
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-btn
                variant="text"
                color="grey-darken-1"
                block
                @click="showOrderDetail = false"
                size="small"
              >
                <v-icon start size="18">mdi-arrow-left</v-icon>
                <span>Seguir Comprando</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Confirmación de Cancelación mejorado -->
    <v-dialog
      v-model="showCancelQuestion"
      max-width="500"
      transition="dialog-transition"
    >
      <v-card class="cancel-dialog">
        <v-card-title class="text-center pt-6">
          <v-icon size="60" color="warning">mdi-alert-circle-outline</v-icon>
        </v-card-title>
        
        <v-card-title class="text-center text-h5 px-6">
          ¿Cancelar Pedido?
        </v-card-title>
        
        <v-card-text class="text-center px-6 pb-6">
          Se eliminarán todos los productos seleccionados. ¿Estás seguro que deseas cancelarlo?
        </v-card-text>
        
        <v-card-actions class="pa-4 justify-center">
          <v-btn
            variant="outlined"
            color="grey-darken-1"
            size="large"
            min-width="120"
            @click="showCancelQuestion = false"
          >
            No, Volver
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            size="large"
            min-width="120"
            @click="cancelOrder"
          >
            Sí, Cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <v-dialog
      v-model="showCountrySelection"
      persistent
      width="auto"
    >
      <CountrySelector
        :current="SHOP.country"
        @cancel="showCountrySelection = false"
        @confirm="setCountry"
      />
    </v-dialog>
    
    <IntegraDulceDeLechePromoModal
      v-if="SHOP.isArg && !showCountrySelection"
      @accept="scrollToIntegraDulceDeLeche"
    />
  </v-app>
</template>

<script setup>
import { onMounted, computed } from 'vue';
import { useOrder } from '@/store/order';
import { useShop } from '@/store/shop';
import { ref, watchEffect } from 'vue';
import IntegraDulceDeLechePromoModal from '@/components/promos/IntegraDulceDeLechePromoModal.vue';

import BurgerCard from '@/components/BurgerCard';
import DressingCard from '@/components/DressingCard';
import OtherCard from '@/components/OtherCard';
import CountrySelector from '@/components/CountrySelector.vue';

import { resolveCategoryName } from '@/helpers/category-name-resolver';
import { ARG, URU } from '@/static/country-code';

const ORDER = useOrder();
const SHOP = useShop();

const showProductDetail = ref(false);
const showOrderDetail = ref(false);
const showCancelQuestion = ref(false);
const showCountrySelection = ref(false);

const productDetail = ref({
  title: '',
  price: '',
  description: '',
});

const setProductDetail = (product) => {
  const categoryName = resolveCategoryName(product.category);
  productDetail.value.title = `${!categoryName ? '' : `${categoryName} de` } ${product.name}`;
  productDetail.value.price = product.price;
  productDetail.value.description = product.description;
  showProductDetail.value = !showProductDetail.value;

  showOrderDetail.value = false;
};

const toggleOrderDetail = () => {
  showOrderDetail.value = !showOrderDetail.value;

  if (!showOrderDetail.value) return;

  showProductDetail.value = false;
};

const cancelOrder = () => {
  ORDER.reset();
  showCancelQuestion.value = false;
};

const setCountry = (data) => {
  showCountrySelection.value = false;
  ORDER.reset();
  SHOP.setCountry(data.value);
}

const scrollToIntegraDulceDeLeche = () => {
  setTimeout(() => {
    const element = document.getElementById('arg-others-8');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 500);
}

const scrollToCategory = (categoryId) => {
  const element = document.getElementById(categoryId);
  if (element) {
    const offset = 120; // Altura del header + nav sticky
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
}

const activeCategoriesCount = computed(() => {
  let count = 0;
  if (SHOP.burgers.length > 0) count++;
  if (SHOP.dressings.length > 0) count++;
  if (SHOP.others.length > 0) count++;
  return count;
})

const trySetCountryByQueryParam = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const country = urlParams.get('country');
  let assigned = false;

  if (!country) return assigned;

  switch (country) {
    case ARG:
      SHOP.setCountry(ARG);
      assigned = true;
      break;
    case URU:
      SHOP.setCountry(URU);
      assigned = true;
      break;
    default:
      break;
  }

  return assigned;
}

watchEffect(() => {
  if (ORDER.price) return;

  showOrderDetail.value = false;
});

onMounted(async () => {
  const setted = trySetCountryByQueryParam();
  if (!setted) await SHOP.init();

  showCountrySelection.value = !SHOP.hasCountrySelected;
});
</script>

<style scoped>
/* Header */
.header-bar {
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.brand-logo {
  height: 50px;
  margin-left: 8px;
}

.cart-badge-fixed {
  margin-right: 12px;
}

.cart-badge-fixed :deep(.v-badge__badge) {
  font-weight: 700;
  font-size: 0.75rem;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  top: 8px;
  right: 8px;
}

.cart-button {
  transition: transform 0.2s;
}

.cart-button:hover {
  transform: scale(1.1);
}

.country-selector {
  margin-right: 8px;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #2E7D32 0%, #4CAF50 50%, #66BB6A 100%);
  padding: 70px 20px !important;
  margin-top: 64px;
  margin-bottom: 32px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="2" fill="white" opacity="0.1"/></svg>');
  background-size: 40px 40px;
  opacity: 0.3;
}

.hero-content {
  color: white;
  position: relative;
  z-index: 1;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.hero-title {
  font-size: clamp(2.2rem, 6vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 16px;
  text-shadow: 3px 3px 6px rgba(0,0,0,0.3);
  line-height: 1.2;
}

.hero-subtitle {
  font-size: clamp(1.1rem, 3vw, 1.6rem);
  margin-bottom: 12px;
  opacity: 0.95;
  font-weight: 500;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
}

.hero-description {
  font-size: clamp(0.95rem, 2.5vw, 1.2rem);
  margin-bottom: 28px;
  opacity: 0.9;
  font-weight: 400;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.2);
}

.badges-container {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.hero-chip {
  font-weight: 700;
  font-size: 1rem;
  padding: 24px 20px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  border: 2px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.hero-chip:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.3);
}

.chip-vegano {
  background: rgba(76, 175, 80, 0.95) !important;
  color: white !important;
}

.chip-congelado {
  background: rgba(33, 150, 243, 0.95) !important;
  color: white !important;
}

.chip-saludable {
  background: rgba(255, 152, 0, 0.95) !important;
  color: white !important;
}

.chip-text {
  font-size: 1rem;
  font-weight: 700;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
}

.trust-indicators {
  display: flex;
  justify-content: center;
  gap: 32px;
  flex-wrap: wrap;
}

.trust-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
}

.trust-icon {
  background: rgba(255, 255, 255, 0.25);
  padding: 12px;
  border-radius: 50%;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

/* Navegaci\u00f3n entre categor\u00edas */
.categories-nav-sticky {
  position: sticky;
  top: 64px;
  z-index: 999;
  background: white;
  border-bottom: 2px solid #E0E0E0;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  padding: 12px 0;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.categories-nav-content {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.category-nav-chip {
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0 20px;
  height: 44px;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.category-nav-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border-color: currentColor;
}

.category-nav-chip:active {
  transform: translateY(0);
}

/* Main Container */
.main__container {
  margin-top: 0;
  margin-bottom: 20px;
  padding-bottom: 100px;
  max-width: 1400px;
}

/* Category Section */
.category-section {
  margin-bottom: 60px;
  scroll-margin-top: 140px;
}

.category-header {
  display: flex;
  align-items: center;
  margin-bottom: 32px;
  padding: 20px;
  background: white;
  border-radius: 16px;
  border-left: 6px solid #4CAF50;
  box-shadow: 0 2px 16px rgba(0,0,0,0.1);
}

.category-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2E7D32;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.category-subtitle {
  font-size: 1rem;
  color: #666;
  margin: 4px 0 0 0;
}

/* Footer */
.footer-section {
  background: linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%);
  color: white;
  padding: 32px 0;
  padding-bottom: 100px;
  margin-top: 20px;
}

/* Bottom Navigation - Diseño Vertical Limpio */
.bottom-nav-enhanced {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #E0E0E0;
  padding: 12px;
  z-index: 1000;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.08);
}

.bottom-nav-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Resumen de orden limpio */
.order-summary-clean {
  background: #F5F5F5;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.order-summary-clean:hover {
  background: #EEEEEE;
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.order-summary-clean:active {
  transform: scale(0.98);
}

.order-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-items {
  font-size: 0.9rem;
  font-weight: 500;
  color: #666;
}

.order-total {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2E7D32;
}

/* Botones de acción limpios */
.action-buttons-clean {
  display: flex;
  gap: 8px;
}

.action-btn-clean {
  flex: 1;
  height: 44px !important;
  padding: 0 16px !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  border: 2px solid transparent !important;
  transition: all 0.2s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  white-space: nowrap !important;
  overflow: hidden !important;
}

.action-btn-clean .v-btn__content {
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
  white-space: nowrap !important;
}

.action-btn-clean .v-icon {
  font-size: 20px !important;
  flex-shrink: 0;
}

.action-btn-clean span {
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
}

.cancel-btn-clean {
  background: white !important;
  color: #D32F2F !important;
  border-color: #D32F2F !important;
  box-shadow: none !important;
}

.cancel-btn-clean:hover {
  background: #FFEBEE !important;
  border-color: #C62828 !important;
}

.confirm-btn-clean {
  background: #4CAF50 !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.25) !important;
}

.confirm-btn-clean:hover {
  background: #43A047 !important;
  box-shadow: 0 3px 10px rgba(76, 175, 80, 0.35) !important;
}

.bottom-nav-content-single {
  width: 100%;
  padding: 0;
}

/* WhatsApp Button Styling */
.whatsapp-button {
  width: 100% !important;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%) !important;
  color: white !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  padding: 14px 16px !important;
  border-radius: 0 !important;
  box-shadow: 0 4px 16px rgba(37, 211, 102, 0.4) !important;
  transition: all 0.3s ease;
  min-height: auto !important;
  height: auto !important;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center !important;
  white-space: nowrap !important;
}

.whatsapp-button:hover {
  box-shadow: 0 6px 24px rgba(37, 211, 102, 0.6) !important;
  transform: translateY(-2px);
}

.whatsapp-icon {
  animation: pulse 2s infinite;
}

.whatsapp-text {
  font-size: 1rem;
  font-weight: 600;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* Modal Styles */
.product-detail-header {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  font-size: 1.5rem;
  font-weight: 700;
}

.price-highlight {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  padding: 16px;
  border-radius: 16px;
  border: 2px solid #4CAF50;
}

.price-large {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2E7D32;
}

.price-unit-text {
  font-size: 1rem;
  color: #666;
}

.description-section {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 12px;
}

.description-text {
  color: #444;
  line-height: 1.6;
}

.order-detail-header {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  padding: 20px !important;
  border-bottom: 3px solid #4CAF50;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
}

.header-title {
  display: flex;
  align-items: center;
  font-size: 1.4rem;
  font-weight: 700;
  color: #2E7D32;
  flex-shrink: 0;
}

.total-price-display {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  background: white;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border: 2px solid #4CAF50;
}

.total-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #757575;
  letter-spacing: 1px;
  margin-bottom: 2px;
}

.total-amount {
  font-size: 2rem;
  font-weight: 800;
  color: #2E7D32;
  line-height: 1;
}

.total-chip {
  font-weight: 700;
}

.order-item {
  padding: 12px 16px;
}

.order-item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
}

.item-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.item-details {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
  margin-bottom: 4px;
}

.item-price {
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.item-actions-cart {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.order-actions-footer {
  background: #fafafa;
  border-top: 2px solid #e0e0e0;
}

.confirm-btn {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.3px;
}

.cancel-dialog .v-card-title {
  display: flex;
  justify-content: center;
}

/* Responsive */
@media (max-width: 600px) {
  .hero-section {
    padding: 50px 16px !important;
    margin-top: 56px;
  }
  
  .brand-logo {
    height: 40px;
    margin-left: 4px;
  }
  
  .cart-badge-fixed {
    margin-right: 8px;
  }
  
  .cart-badge-fixed :deep(.v-badge__badge) {
    font-size: 0.7rem;
    min-width: 18px;
    height: 18px;
    top: 6px;
    right: 6px;
  }
  
  .country-selector {
    margin-right: 4px;
  }
  
  /* Navegaci\u00f3n categor\u00edas responsive */
  .categories-nav-sticky {
    top: 56px;
    padding: 8px 0;
  }
  
  .categories-nav-content {
    gap: 8px;
    padding: 0 8px;
  }
  
  .category-nav-chip {
    font-size: 0.85rem;
    height: 38px;
    padding: 0 14px;
  }
  
  .category-nav-chip .v-icon {
    font-size: 18px;
  }
  
  .category-section {
    scroll-margin-top: 120px;
  }
  
  .badges-container {
    gap: 8px;
  }
  
  .hero-chip {
    padding: 20px 16px;
    font-size: 0.9rem;
  }
  
  .chip-text {
    font-size: 0.9rem;
  }
  
  .trust-indicators {
    gap: 24px;
  }
  
  .trust-item {
    font-size: 0.85rem;
  }
  
  /* Footer sticky responsive */
  .bottom-nav-enhanced {
    padding: 10px 8px;
  }
  
  .order-summary-clean {
    padding: 8px 12px;
  }
  
  .order-items {
    font-size: 0.85rem;
  }
  
  .order-total {
    font-size: 1.1rem;
  }
  
  .action-buttons-clean {
    gap: 6px;
  }
  
  .action-btn-clean {
    height: 40px !important;
    padding: 0 10px !important;
    font-size: 0.85rem !important;
  }
  
  .action-btn-clean .v-icon {
    font-size: 18px !important;
  }
  
  .main__container {
    padding-bottom: 80px;
  }
  
  .footer-section {
    padding-bottom: 80px;
  }
  
  .category-header {
    flex-direction: column;
    text-align: center;
    padding: 16px;
  }
  
  .category-header .v-icon {
    margin-bottom: 12px;
    margin-right: 0 !important;
  }
  
  .total-price-display {
    padding: 8px 14px;
  }
  
  .total-label {
    font-size: 0.65rem;
  }
  
  .total-amount {
    font-size: 1.5rem;
  }
  
  .header-content {
    gap: 10px;
  }
  
  .header-title {
    font-size: 1.2rem;
  }
  
  .price-large {
    font-size: 2rem;
  }
  
  .whatsapp-text {
    font-size: 1rem;
  }
}
</style>
